/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { MgtTemplatedComponent, mgtHtml, customElement } from '@microsoft/mgt-element';
import { strings } from './strings';
import { fluentCombobox, fluentOption } from '@fluentui/web-components';
import { registerFluentComponents } from '../../utils/FluentComponents';
import '../../styles/style-helper';
registerFluentComponents(fluentCombobox, fluentOption);
/**
 * Web component that allows a single entity pick from a generic endpoint from Graph. Uses mgt-get.
 *
 * @fires {CustomEvent<any>} selectionChanged - Fired when an option is clicked/selected
 * @export
 * @class MgtPicker
 * @extends {MgtTemplatedComponent}
 */
// @customElement('mgt-picker')
let MgtPicker = class MgtPicker extends MgtTemplatedComponent {
    get strings() {
        return strings;
    }
    constructor() {
        super();
        /**
         * Api version to use for request
         *
         * @type {string}
         * @memberof MgtPicker
         */
        this.version = 'v1.0';
        /**
         * Maximum number of pages to get for the resource
         * default = 3
         * if <= 0, all pages will be fetched
         *
         * @type {number}
         * @memberof MgtPicker
         */
        this.maxPages = 3;
        /**
         * The scopes to request
         *
         * @type {string[]}
         * @memberof MgtPicker
         */
        this.scopes = [];
        /**
         * Enables cache on the response from the specified resource
         * default = false
         *
         * @type {boolean}
         * @memberof MgtPicker
         */
        this.cacheEnabled = false;
        /**
         * Invalidation period of the cache for the responses in milliseconds
         *
         * @type {number}
         * @memberof MgtPicker
         */
        this.cacheInvalidationPeriod = 0;
        this.placeholder = this.strings.comboboxPlaceholder;
        this.entityType = null;
        this.keyName = null;
        this.isRefreshing = false;
    }
    /**
     * Refresh the data
     *
     * @param {boolean} [hardRefresh=false]
     * if false (default), the component will only update if the data changed
     * if true, the data will be first cleared and reloaded completely
     * @memberof MgtPicker
     */
    refresh(hardRefresh = false) {
        this.isRefreshing = true;
        if (hardRefresh) {
            this.clearState();
        }
        this.requestStateUpdate(hardRefresh);
    }
    /**
     * Clears the state of the component
     *
     * @protected
     * @memberof MgtPicker
     */
    clearState() {
        this.response = null;
        this.error = null;
    }
    /**
     * Invoked on each update to perform rendering the picker. This method must return
     * a lit-html TemplateResult. Setting properties inside this method will *not*
     * trigger the element to update.
     */
    render() {
        var _a;
        if (this.isLoadingState && !this.response) {
            return this.renderTemplate('loading', null);
        }
        else if (this.hasTemplate('error')) {
            return this.renderTemplate('error', this.error ? this.error : null, 'error');
        }
        else if (this.hasTemplate('no-data')) {
            return this.renderTemplate('no-data', null);
        }
        return ((_a = this.response) === null || _a === void 0 ? void 0 : _a.length) > 0 ? this.renderPicker() : this.renderGet();
    }
    /**
     * Render picker.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPicker
     */
    renderPicker() {
        return mgtHtml `
      <fluent-combobox id="combobox" autocomplete="list" placeholder=${this.placeholder}>
        ${this.response.map(item => html `
          <fluent-option value=${item.id} @click=${e => this.handleClick(e, item)}> ${item[this.keyName]} </fluent-option>`)}
      </fluent-combobox>
     `;
    }
    /**
     * Render picker.
     *
     * @protected
     * @returns {TemplateResult}
     * @memberof MgtPicker
     */
    renderGet() {
        return mgtHtml `
      <mgt-get 
        resource=${this.resource}
        version=${this.version} 
        scopes=${this.scopes} 
        max-pages=${this.maxPages} 
        ?cache-enabled=${this.cacheEnabled}
        ?cache-invalidation-period=${this.cacheInvalidationPeriod}>
      </mgt-get>`;
    }
    /**
     * load state into the component.
     *
     * @protected
     * @returns
     * @memberof MgtPicker
     */
    loadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.response) {
                let parent = this.renderRoot.querySelector('mgt-get');
                parent.addEventListener('dataChange', (e) => this.handleDataChange(e));
            }
            this.isRefreshing = false;
        });
    }
    handleDataChange(e) {
        let response = e.detail.response.value;
        let error = e.detail.error ? e.detail.error : null;
        this.response = response;
        this.error = error;
    }
    handleClick(e, item) {
        this.fireCustomEvent('selectionChanged', item);
    }
};
__decorate([
    property({
        attribute: 'resource',
        type: String
    }),
    __metadata("design:type", String)
], MgtPicker.prototype, "resource", void 0);
__decorate([
    property({
        attribute: 'version',
        type: String
    }),
    __metadata("design:type", String)
], MgtPicker.prototype, "version", void 0);
__decorate([
    property({
        attribute: 'max-pages',
        type: Number
    }),
    __metadata("design:type", Number)
], MgtPicker.prototype, "maxPages", void 0);
__decorate([
    property({
        attribute: 'placeholder',
        type: String
    }),
    __metadata("design:type", String)
], MgtPicker.prototype, "placeholder", void 0);
__decorate([
    property({
        attribute: 'key-name',
        type: String
    }),
    __metadata("design:type", String)
], MgtPicker.prototype, "keyName", void 0);
__decorate([
    property({
        attribute: 'entity-type',
        type: String
    }),
    __metadata("design:type", String)
], MgtPicker.prototype, "entityType", void 0);
__decorate([
    property({
        attribute: 'scopes',
        converter: value => {
            return value ? value.toLowerCase().split(',') : null;
        }
    }),
    __metadata("design:type", Array)
], MgtPicker.prototype, "scopes", void 0);
__decorate([
    property({
        attribute: 'cache-enabled',
        type: Boolean
    }),
    __metadata("design:type", Boolean)
], MgtPicker.prototype, "cacheEnabled", void 0);
__decorate([
    property({
        attribute: 'cache-invalidation-period',
        type: Number
    }),
    __metadata("design:type", Number)
], MgtPicker.prototype, "cacheInvalidationPeriod", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], MgtPicker.prototype, "response", void 0);
__decorate([
    state(),
    __metadata("design:type", Array)
], MgtPicker.prototype, "error", void 0);
MgtPicker = __decorate([
    customElement('picker'),
    __metadata("design:paramtypes", [])
], MgtPicker);
export { MgtPicker };
